import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Alert, AlertDescription } from '@/components/ui/alert'
import DatabaseAssignment from '@/components/admin/DatabaseAssignment'
import EditRestaurantForm from '@/components/admin/EditRestaurantForm'
import CreateRestaurantForm from '@/components/admin/CreateRestaurantForm'
import {
  Building2,
  Plus,
  Search,
  Database,
  CheckCircle,
  AlertTriangle,
  Eye,
  Edit,
  MoreHorizontal,
  Loader2,
  Wifi,
  WifiOff
} from 'lucide-react'
import { formatDate } from '@/lib/utils'
import { ApiService } from '@/services/api'
import type { Restaurant } from '@/types'


export default function RestaurantManagementScreen() {
  const [restaurants, setRestaurants] = useState<Restaurant[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null)
  const [editingRestaurant, setEditingRestaurant] = useState<Restaurant | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isRefreshingActivity, setIsRefreshingActivity] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load restaurants on component mount
  useEffect(() => {
    loadRestaurants()
  }, [])

  const loadRestaurants = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await ApiService.getAllRestaurants()
      if (response.success) {
        setRestaurants(response.data)
        // Refresh activity status in background after loading
        refreshActivity(response.data)
      } else {
        setError(response.message || 'Failed to load restaurants')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load restaurants')
    } finally {
      setIsLoading(false)
    }
  }

  const refreshActivity = async (currentRestaurants?: Restaurant[]) => {
    setIsRefreshingActivity(true)
    try {
      const activityResponse = await ApiService.refreshRestaurantsActivity()
      if (activityResponse.success) {
        const activityMap = new Map(
          activityResponse.data.map((a) => [a.id, a.lastActivityDate])
        )
        setRestaurants((prev) =>
          (currentRestaurants || prev).map((r) =>
            activityMap.has(r._id)
              ? { ...r, lastActivityDate: activityMap.get(r._id) ?? null }
              : r
          )
        )
      }
    } catch {
      // Activity refresh failure is non-critical, silently ignore
    } finally {
      setIsRefreshingActivity(false)
    }
  }

  const getActivityBadge = (restaurant: Restaurant) => {
    if (!restaurant.databaseName) return null

    if (restaurant.lastActivityDate === undefined) {
      // Still loading
      return <Badge variant="outline" className="text-xs">Checking...</Badge>
    }

    if (!restaurant.lastActivityDate) {
      return (
        <Badge variant="outline" className="text-xs flex items-center gap-1 w-fit">
          <WifiOff className="h-3 w-3" />
          No bills
        </Badge>
      )
    }

    const diffMs = Date.now() - new Date(restaurant.lastActivityDate).getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays < 1) {
      return (
        <Badge variant="success" className="text-xs flex items-center gap-1 w-fit">
          <Wifi className="h-3 w-3" />
          Online
        </Badge>
      )
    }

    return (
      <Badge variant="destructive" className="text-xs flex items-center gap-1 w-fit">
        <WifiOff className="h-3 w-3" />
        Offline {diffDays}d
      </Badge>
    )
  }

  const filteredRestaurants = restaurants.filter(restaurant =>
    restaurant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    restaurant.ownerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    restaurant.email.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleRestaurantUpdate = (updatedRestaurant: Restaurant) => {
    setRestaurants(prev => 
      prev.map(r => r._id === updatedRestaurant._id ? updatedRestaurant : r)
    )
    setSelectedRestaurant(updatedRestaurant)
  }

  const handleEditRestaurant = (restaurant: Restaurant) => {
    setEditingRestaurant(restaurant)
  }

  const handleCreateSuccess = (newRestaurant: Restaurant) => {
    setRestaurants(prev => [newRestaurant, ...prev])
    setShowCreateForm(false)
  }

  const getStatusBadge = (restaurant: Restaurant) => {
    if (!restaurant.databaseName) {
      return <Badge variant="destructive">No Database</Badge>
    }
    
    switch (restaurant.subscriptionStatus) {
      case 'active':
        return <Badge variant="success">Active</Badge>
      case 'expired':
        return <Badge variant="destructive">Expired</Badge>
      case 'suspended':
        return <Badge variant="warning">Suspended</Badge>
      default:
        return <Badge variant="outline">Unknown</Badge>
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Restaurant Management</h1>
          <p className="text-muted-foreground">
            Manage restaurant accounts and database assignments
          </p>
        </div>
        <Button onClick={() => setShowCreateForm(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Restaurant
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Search & Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search restaurants by name, owner, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              {filteredRestaurants.length} of {restaurants.length} restaurants
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {error}
            <Button 
              variant="outline" 
              size="sm" 
              className="ml-2"
              onClick={loadRestaurants}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Restaurants Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Building2 className="h-5 w-5" />
            <span>Restaurants</span>
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Restaurant</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Database</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="flex items-center gap-1">
                  Activity
                  {isRefreshingActivity && <Loader2 className="h-3 w-3 animate-spin" />}
                </TableHead>
                <TableHead>Subscription</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRestaurants.map((restaurant) => (
                <TableRow key={restaurant._id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{restaurant.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {restaurant.address.city}, {restaurant.address.state}
                        {restaurant.country ? ` · ${restaurant.country}` : ''}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{restaurant.ownerName}</div>
                      <div className="text-sm text-muted-foreground">{restaurant.email}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {restaurant.databaseName ? (
                      <div className="flex items-center space-x-2">
                        <Database className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-mono">{restaurant.databaseName}</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <AlertTriangle className="h-4 w-4 text-orange-600" />
                        <span className="text-sm text-muted-foreground">Not Assigned</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(restaurant)}
                  </TableCell>
                  <TableCell>
                    {getActivityBadge(restaurant)}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div>{formatDate(restaurant.subscriptionEndDate)}</div>
                      <div className="text-muted-foreground">
                        {restaurant.paymentStatus}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedRestaurant(restaurant)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleEditRestaurant(restaurant)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Database Assignment Panel */}
      {selectedRestaurant && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              Database Management - {selectedRestaurant.name}
            </h2>
            <Button
              variant="outline"
              onClick={() => setSelectedRestaurant(null)}
            >
              Close
            </Button>
          </div>
          
          <DatabaseAssignment
            restaurant={selectedRestaurant}
            onUpdate={handleRestaurantUpdate}
          />
        </div>
      )}

      {/* Statistics */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{restaurants.length}</p>
                <p className="text-sm text-muted-foreground">Total Restaurants</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <Database className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-2xl font-bold">
                  {restaurants.filter(r => r.databaseName).length}
                </p>
                <p className="text-sm text-muted-foreground">Databases Assigned</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-2xl font-bold">
                  {restaurants.filter(r => r.subscriptionStatus === 'active').length}
                </p>
                <p className="text-sm text-muted-foreground">Active Subscriptions</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Connection Info */}
      <Alert>
        <Database className="h-4 w-4" />
        <AlertDescription>
          <strong>MongoDB Cluster:</strong> ordorecone.equ25.mongodb.net<br />
          Each restaurant uses a separate database (e.g., , ordore-restaurant2) 
          for complete data isolation. Assign databases carefully as this determines data access.
        </AlertDescription>
      </Alert>

      {/* Create Restaurant Form */}
      <CreateRestaurantForm
        open={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Edit Restaurant Form */}
      <EditRestaurantForm
        restaurant={editingRestaurant}
        open={!!editingRestaurant}
        onClose={() => setEditingRestaurant(null)}
        onUpdate={handleRestaurantUpdate}
      />
    </div>
  )
}